// Data plynulého seznamu po dávkách (koncepce A, vzor Kontakty – bod C1): pole o délce celého seznamu, nenačtené pozice
// jsou zástupci (virtual.ts), dávky se dočítají podle toho, co je vidět (ensure), nebo pro Shift-výběr (loadRange).
// Společné pro seznamy jádra (ListView) i vlastní seznamy aplikací (Pošta). Vykreslení řeší komponenta (IwTable, IwCards,
// seznam pošty) – PagedRows jen drží řádky a hlásí změny.
import type { Row } from './table';
import { placeholder, isPh } from './virtual';

export interface PagedSource<T extends object> {
  pageSize: number;
  rowId: string;
  /** Dávka page (od 1) – stejný dotaz jako první dávka. */
  fetch: (page: number) => Promise<{ rows: T[]; total: number }>;
  /** Dočtené řádky jsou v poli – překreslit. */
  onUpdate: () => void;
  /** Server hlásí jiný celkový počet (přibylo / ubylo) – načíst znovu se zachováním místa. */
  onTotalChanged: () => void;
  /** Chyba dočtení – hlásí se jednou za načtení (posouvání nesmí chrlit hlášky). */
  onError: (e: unknown) => void;
  /** Změna rozpracovaných dávek (stavový řádek „načítám…“). */
  onBusy?: () => void;
}

export class PagedRows<T extends object> {
  rows: T[] = [];
  total = 0;
  /** dávky načtené po posledním obnovení (ostatní řádky můžou být zástupci nebo starší data) */
  private fresh = new Set<number>();
  private inflight = new Map<number, Promise<void>>();
  private failed = new Map<number, number>();
  private seq = 0;
  private errSeq = -1;

  constructor(private src: PagedSource<T>) {}

  /**
   * Nová první dávka. keep = obnovení téhož dotazu (živá změna, F5): dosavadní řádky zůstanou na místě, viditelné dávky
   * se jen vymění (seznam nezešedne); jinak je zbytek zástupci.
   */
  reset(total: number, first: T[], keep: boolean): void {
    this.seq++;
    const old = keep ? this.rows : [];
    this.total = total;
    this.rows = Array.from({ length: total }, (_, i) => (i < old.length ? (old[i] as T) : (placeholder(i, this.src.rowId) as unknown as T)));
    first.forEach((r, i) => { if (i < total) this.rows[i] = r; });
    this.fresh = new Set([1]);
    this.inflight.clear();
    this.failed.clear();
    this.src.onBusy?.();
  }

  /** Řádky odebrané hned (smazání, přesun): seznam se posune bez čekání na server, viditelné dávky se dočtou znovu. */
  remove(ids: Iterable<string>): void {
    const gone = new Set(ids);
    this.rows = this.rows.filter((r) => isPh(r as Row) || !gone.has(String((r as Record<string, unknown>)[this.src.rowId])));
    this.total = this.rows.length;
    this.seq++; // rozpracované dávky by mířily na staré pozice
    this.fresh.clear();
    this.inflight.clear();
    this.src.onBusy?.();
  }

  /** Načtené řádky (bez zástupců). */
  loaded(): T[] {
    return this.rows.filter((r) => !isPh(r as Row));
  }

  busy(): boolean {
    return this.inflight.size > 0;
  }

  /** Vykreslené okno potřebuje řádky from..to → chybějící (nebo po obnovení neověřené) dávky načíst. */
  ensure(from: number, to: number): void {
    const P = this.src.pageSize;
    if (to <= from) return;
    const now = Date.now();
    for (let p = Math.floor(from / P) + 1; p <= Math.floor((Math.min(to, this.total) - 1) / P) + 1; p++) {
      if (!this.fresh.has(p) && !this.inflight.has(p) && now - (this.failed.get(p) ?? 0) > 5000) void this.fetchPage(p);
    }
  }

  /** Shift-výběr / skok přes nenačtené: dočíst všechny dávky rozsahu (nejvýš 5000 řádků). */
  async loadRange(from: number, to: number): Promise<void> {
    const P = this.src.pageSize;
    const end = Math.min(to, from + 5000, this.total);
    const wait: Promise<void>[] = [];
    for (let p = Math.floor(from / P) + 1; p <= Math.floor((end - 1) / P) + 1; p++) {
      if (this.fresh.has(p) && !this.rows.slice((p - 1) * P, p * P).some((r) => isPh(r as Row))) continue;
      wait.push(this.inflight.get(p) ?? this.fetchPage(p));
    }
    await Promise.all(wait);
  }

  private fetchPage(page: number): Promise<void> {
    const run = this.doFetch(page);
    this.inflight.set(page, run);
    this.src.onBusy?.();
    return run;
  }

  private async doFetch(page: number): Promise<void> {
    const seq = this.seq;
    const P = this.src.pageSize;
    try {
      const res = await this.src.fetch(page);
      if (seq !== this.seq) return; // mezitím jiný dotaz / obnovení
      if (res.total !== this.total) {
        this.src.onTotalChanged();
        return;
      }
      res.rows.forEach((r, i) => { const at = (page - 1) * P + i; if (at < this.total) this.rows[at] = r; });
      this.fresh.add(page);
      this.failed.delete(page);
      this.src.onUpdate();
    } catch (e) {
      if (seq !== this.seq) return;
      this.failed.set(page, Date.now()); // znovu až po chvíli
      if (this.errSeq !== seq) {
        this.errSeq = seq;
        this.src.onError(e);
      }
    } finally {
      if (seq === this.seq) {
        this.inflight.delete(page);
        this.src.onBusy?.();
      }
    }
  }
}
